import React, { useState, useEffect } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import ReactQuill from "react-quill";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db, COLLECTIONS } from "../../../firebase";
import { NOTE_THEMES } from "../constants"; // adapte le chemin selon ton projet

const BIBLE_CATEGORY = "Lecture biblique";

export default function QuickBibleNoteForm({ userId, onClose }) {
    // Récupérer le thème stocké ou utiliser le premier par défaut
    const storedTheme = localStorage.getItem("quickBibleNoteTheme") || NOTE_THEMES[0].id;

    const [note, setNote] = useState({
        title: "",
        content: "",
        theme: storedTheme, // thème par défaut ou sauvegardé
    });
    const [saving, setSaving] = useState(false);
    const [showThemeSelector, setShowThemeSelector] = useState(false);

    // Mettre à jour localStorage à chaque changement de thème
    useEffect(() => {
        localStorage.setItem("quickBibleNoteTheme", note.theme);
    }, [note.theme]);

    const handleSave = async () => {
        if (!note.content.trim()) return;

        setSaving(true);
        await addDoc(collection(db, COLLECTIONS.NOTES), {
            title: note.title || "Note biblique",
            content: note.content,
            category: BIBLE_CATEGORY,
            theme: note.theme,
            userId,
            createdAt: serverTimestamp(),
        });
        setSaving(false);
        onClose();
    };

    // Trouver le thème actif pour l'affichage de la petite carrée
    const activeTheme = NOTE_THEMES.find((t) => t.id === note.theme);

    return (
        <Modal show onHide={onClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title style={{ display: "flex", alignItems: "center" }}>
                    📖 Note de lecture biblique
                    {activeTheme && (
                        <span
                            className="active-theme-indicator"
                            style={{
                                background: activeTheme.background,
                                border: `2px solid ${activeTheme.textSecondary}`,
                            }}
                            title={activeTheme.label}
                        />
                    )}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form.Control
                    placeholder="Titre (optionnel)"
                    value={note.title}
                    onChange={(e) => setNote({ ...note, title: e.target.value })}
                    className="mb-2"
                />

                <div className="mb-3">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setShowThemeSelector(!showThemeSelector)}
                    >
                        {showThemeSelector ? "Masquer thèmes" : "Modifier thème"}
                    </Button>

                    {showThemeSelector && (
                        <div className="d-flex flex-wrap gap-2 mt-2">
                            {NOTE_THEMES.map((t) => (
                                <div
                                    key={t.id}
                                    className={`theme-preview ${t.id === note.theme ? "active" : ""}`}
                                    style={{
                                        background: t.background,
                                        color: t.text,
                                        padding: "8px 12px",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        border: `2px solid ${t.id === note.theme ? t.textSecondary : "transparent"}`,
                                        boxShadow: t.id === note.theme ? `0 0 15px ${t.textSecondary}80` : "none",
                                        transition: "all 0.3s ease",
                                        fontWeight: t.id === note.theme ? "bold" : "normal",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        position: "relative",
                                    }}
                                    onClick={() => setNote({ ...note, theme: t.id })}
                                    title={t.label}
                                >
                                    {t.id === note.theme && "✓"}
                                    {t.id === note.theme && <span className="pulse-ring" />}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <ReactQuill
                    theme="snow"
                    value={note.content}
                    onChange={(content) => setNote({ ...note, content })}
                    style={{ height: "180px", marginBottom: "20px" }}
                />
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose}>
                    Annuler
                </Button>
                <Button variant="success" onClick={handleSave} disabled={saving}>
                    Sauvegarder
                </Button>
            </Modal.Footer>

            {/* Styles pour le thème actif et animation pulse */}
            <style>{`
        .active-theme-indicator {
          display: inline-block;
          width: 20px;
          height: 20px;
          border-radius: 4px;
          margin-left: 10px;
          position: relative;
        }

        .pulse-ring {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 120%;
          height: 120%;
          transform: translate(-50%, -50%);
          border-radius: 6px;
          border: 2px solid rgba(0,0,0,0.1);
          box-shadow: 0 0 15px rgba(0,0,0,0.1);
          animation: pulse 1.5s infinite;
          z-index: -1;
        }

        @keyframes pulse {
          0% {
            transform: translate(-50%, -50%) scale(0.9);
            opacity: 0.4;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.1);
            opacity: 0.2;
          }
          100% {
            transform: translate(-50%, -50%) scale(0.9);
            opacity: 0.4;
          }
        }
      `}</style>
        </Modal>
    );
}
